import {
  DELIVERY_COMPARE_BASIS_LABELS,
  DELIVERY_COST_STATUS_LABELS,
  DELIVERY_SALES_SOURCE_LABELS,
  DELIVERY_STATUS_LABELS,
  type Delivery,
  type TankStatus,
} from "@station/contracts";
import { Decimal, Money } from "@station/core";
import {
  Alert,
  Button,
  Card,
  cn,
  Dialog,
  FormField,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Check, Download, FileText, Play, Square, TriangleAlert, X } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { base64ToBlob, downloadBlob } from "../../../../features/accounting/shared.js";
import { Field, Section } from "../../../../features/expenses/shared.js";
import { DeliveryDespatchForm } from "../../../../features/fuel/delivery-form.js";
import {
  initialReadingRows,
  ManualSalesRows,
  TankReadingRows,
} from "../../../../features/fuel/delivery-readings.js";
import {
  canCancelDelivery,
  canEditDespatch,
  currentDeliveryStep,
  DELIVERY_STATUS_TONE,
  DELIVERY_STEPS,
  type DeliveryStep,
  deliveryTankIds,
  deliveryTotals,
  formatCountdown,
  isDeliveryClosed,
  type ManualSalesDraft,
  manualSalesToBody,
  reachableDeliverySteps,
  readingsToBody,
  settleState,
  type TankReadingDraft,
} from "../../../../features/fuel/delivery-values.js";
import { dateTimeLocalToIso } from "../../../../features/fuel/datetime.js";
import {
  useDelivery,
  useFuelPermissions,
  useFuelSettings,
  usePurchaseInvoices,
  useRefreshFuel,
  useTanks,
} from "../../../../features/fuel/queries.js";
import { FuelPage, Th } from "../../../../features/fuel/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/akaryakit/dolumlar/$id")({ component: DeliveryWizardPage });

/**
 * Tanker dolumu sihirbazı (Prompt 12 §10): irsaliye → öncesi okuma → boşaltma → sonrası okuma →
 * sonuç ve fark → onay / itiraz.
 *
 * Adım İSTEMCİDE saklanmaz, sunucudaki DURUMDAN türetilir: sayfa yenilense, tarayıcı kapansa ya da
 * belge başka bir cihazdan ilerletilse de kullanıcı doğru adımı görür (`useDelivery` 30 sn'de bir ve
 * pencere öne gelince tazeler).
 */
function DeliveryWizardPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useFuelPermissions();
  const delivery = useDelivery(id);
  const title = delivery.data?.deliveryNo ?? t("fuel.deliveries.detail.title");

  return (
    <FuelPage
      title={title}
      breadcrumb={[
        {
          label: t("menu.fuelDeliveries"),
          link: (children, className) => (
            <Link to="/akaryakit/dolumlar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
    >
      {() =>
        !permissions.canRead ? (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        ) : delivery.error ? (
          <Alert tone="danger">{errorMessage(delivery.error)}</Alert>
        ) : !delivery.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <DeliveryWizard delivery={delivery.data} />
        )
      }
    </FuelPage>
  );
}

function litres(value: string | null | undefined, scale = 3): string {
  return value === null || value === undefined ? "—" : `${Decimal.from(value).formatTr(scale)} L`;
}

function DeliveryWizard({ delivery }: { delivery: Delivery }): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  const tanks = useTanks(delivery.stationId);
  const settings = useFuelSettings(delivery.companyId, delivery.stationId);
  const closed = isDeliveryClosed(delivery.status);
  const [step, setStep] = useState<DeliveryStep>(() => currentDeliveryStep(delivery.status) ?? "result");
  const previousStatus = useRef(delivery.status);

  // Durum ilerlediğinde (bu ekrandan ya da başka bir cihazdan) sihirbaz yeni adıma atlar.
  useEffect(() => {
    if (previousStatus.current === delivery.status) return;
    previousStatus.current = delivery.status;
    setStep(currentDeliveryStep(delivery.status) ?? "result");
  }, [delivery.status]);

  const reachable = reachableDeliverySteps(delivery.status);
  const tankItems = useMemo(() => tanks.data?.items ?? [], [tanks.data]);
  const totals = useMemo(() => deliveryTotals(delivery.lines), [delivery.lines]);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{delivery.deliveryNo}</span>
        <StatusBadge tone={DELIVERY_STATUS_TONE[delivery.status]}>
          {DELIVERY_STATUS_LABELS[delivery.status]}
        </StatusBadge>
        {delivery.costStatus === "NONE" ? null : (
          <StatusBadge tone={delivery.costStatus === "FINAL" ? "success" : "warning"}>
            {DELIVERY_COST_STATUS_LABELS[delivery.costStatus]}
          </StatusBadge>
        )}
        <span className="text-sm text-muted-foreground">{delivery.distributorName}</span>
        {delivery.orderId ? (
          <Link
            to="/akaryakit/siparisler/$id"
            params={{ id: delivery.orderId }}
            className="text-sm underline underline-offset-4"
          >
            {delivery.orderNo ?? t("fuel.orders.detail.title")}
          </Link>
        ) : null}
      </div>

      {delivery.cancelReason ? (
        <Alert tone="warning" title={t("fuel.deliveries.detail.cancelledTitle")}>
          {delivery.cancelReason}
        </Alert>
      ) : null}
      {delivery.disputeReason ? (
        <Alert tone="danger" title={t("fuel.deliveries.detail.disputedTitle")}>
          {delivery.disputeReason}
        </Alert>
      ) : null}

      <ol className="flex flex-wrap gap-2" aria-label={t("fuel.deliveries.steps.label")}>
        {DELIVERY_STEPS.map((item, index) => {
          const enabled = reachable.includes(item);
          const active = item === step;
          return (
            <li key={item}>
              <Button
                variant={active ? "primary" : "outline"}
                size="sm"
                disabled={!enabled}
                aria-current={active ? "step" : undefined}
                onClick={() => setStep(item)}
                className={cn(!enabled && "opacity-60")}
              >
                <span aria-hidden="true">{index + 1}.</span>
                {t(`fuel.deliveries.steps.${item}`)}
              </Button>
            </li>
          );
        })}
      </ol>

      {closed ? <Alert tone="info">{t("fuel.deliveries.detail.readOnly")}</Alert> : null}

      {step === "despatch" ? (
        canEditDespatch(delivery.status) && permissions.canManageDeliveries ? (
          <DeliveryDespatchForm
            companyId={delivery.companyId}
            stationId={delivery.stationId}
            delivery={delivery}
            onSaved={() => setStep("before")}
          />
        ) : (
          <DespatchSummary delivery={delivery} />
        )
      ) : null}

      {step === "before" ? <BeforeStep delivery={delivery} tanks={tankItems} /> : null}
      {step === "unload" ? (
        <UnloadStep
          delivery={delivery}
          settleMinutes={settings.data?.settleMinutes ?? null}
          onNext={() => setStep("after")}
        />
      ) : null}
      {step === "after" ? (
        <AfterStep
          delivery={delivery}
          tanks={tankItems}
          settleMinutes={settings.data?.settleMinutes ?? null}
        />
      ) : null}
      {step === "result" ? <ResultStep delivery={delivery} totals={totals} /> : null}
      {step === "confirm" ? <ConfirmStep delivery={delivery} /> : null}

      {canCancelDelivery(delivery.status) && permissions.canManageDeliveries ? (
        <CancelDelivery delivery={delivery} />
      ) : null}
    </div>
  );
}

// ── 1. İrsaliye (salt okunur görünüm) ────────────────────────────────────────
function DespatchSummary({ delivery }: { delivery: Delivery }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("fuel.deliveries.steps.despatch")}>
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("fuel.deliveries.fields.distributor")}>{delivery.distributorName}</Field>
        <Field label={t("fuel.deliveries.fields.despatchNo")}>{delivery.despatchNo ?? "—"}</Field>
        <Field label={t("fuel.deliveries.fields.despatchDate")}>{formatDate(delivery.despatchDate)}</Field>
        <Field label={t("fuel.deliveries.fields.businessDate")}>{formatDate(delivery.businessDate)}</Field>
        {/* KVKK: plaka ve şoför adı yalnızca belgede görünür, listede gösterilmez. */}
        <Field label={t("fuel.deliveries.fields.truckPlate")}>{delivery.truckPlate ?? "—"}</Field>
        <Field label={t("fuel.deliveries.fields.trailerPlate")}>{delivery.trailerPlate ?? "—"}</Field>
        <Field label={t("fuel.deliveries.fields.driverName")}>{delivery.driverName ?? "—"}</Field>
        <Field label={t("fuel.deliveries.fields.seals")}>
          {delivery.sealNumbers.length > 0 ? delivery.sealNumbers.join(", ") : "—"}
        </Field>
      </dl>
      {delivery.note ? <p className="text-sm">{delivery.note}</p> : null}
      <LinesTable delivery={delivery} showMeasured={false} />
    </Section>
  );
}

function LinesTable({ delivery, showMeasured }: { delivery: Delivery; showMeasured: boolean }): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="overflow-x-auto rounded-lg border"
      tabIndex={0}
      role="group"
      aria-label={t("fuel.deliveries.detail.linesLabel")}
    >
      <table className="w-full text-sm">
        <caption className="sr-only">{t("fuel.deliveries.detail.linesLabel")}</caption>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("fuel.deliveries.fields.product")}</Th>
            <Th align="right">{t("fuel.deliveries.fields.documentL")}</Th>
            {showMeasured ? (
              <>
                <Th align="right">{t("fuel.deliveries.fields.measuredL")}</Th>
                <Th align="right">{t("fuel.deliveries.fields.differenceL")}</Th>
                <Th align="right">{t("fuel.deliveries.fields.differencePercent")}</Th>
                <Th align="right">{t("fuel.deliveries.fields.toleranceL")}</Th>
                <Th>{t("fuel.deliveries.fields.withinTolerance")}</Th>
                <Th align="right">{t("fuel.deliveries.fields.unitCost")}</Th>
                <Th align="right">{t("fuel.deliveries.fields.costAmount")}</Th>
              </>
            ) : (
              <Th>{t("fuel.deliveries.fields.tanks")}</Th>
            )}
          </tr>
        </thead>
        <tbody>
          {delivery.lines.map((line) => (
            <tr key={line.id} className="border-t">
              <td className="px-3 py-2">{line.productName}</td>
              <td className="px-3 py-2 text-right">{litres(line.documentL)}</td>
              {showMeasured ? (
                <>
                  <td className="px-3 py-2 text-right">{litres(line.measuredL)}</td>
                  <td className="px-3 py-2 text-right">{litres(line.differenceL)}</td>
                  <td className="px-3 py-2 text-right">
                    {line.differencePercent === null
                      ? "—"
                      : `%${Decimal.from(line.differencePercent).formatTr(3)}`}
                  </td>
                  <td className="px-3 py-2 text-right">{litres(line.toleranceL)}</td>
                  <td className="px-3 py-2">
                    {line.withinTolerance === null ? (
                      "—"
                    ) : (
                      <StatusBadge tone={line.withinTolerance ? "success" : "danger"}>
                        {line.withinTolerance
                          ? t("fuel.deliveries.tolerance.inside")
                          : t("fuel.deliveries.tolerance.outside")}
                      </StatusBadge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {line.unitCost === null ? "—" : `${Decimal.from(line.unitCost).formatTr(4)} ₺/L`}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {line.costAmount === null ? "—" : Money.fromMinor(line.costAmount).formatTr()}
                  </td>
                </>
              ) : (
                <td className="px-3 py-2">{line.tanks.map((tank) => `#${tank.tankNo}`).join(", ") || "—"}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── 2. Boşaltma öncesi okuma ─────────────────────────────────────────────────
function BeforeStep({ delivery, tanks }: { delivery: Delivery; tanks: TankStatus[] }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const tankIds = useMemo(
    () => deliveryTankIds(delivery.lines.map((line) => ({ tankIds: line.tanks.map((tank) => tank.tankId) }))),
    [delivery.lines],
  );
  const [rows, setRows] = useState<TankReadingDraft[]>(() => initialReadingRows(tankIds, new Date(), true));
  const [incomplete, setIncomplete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const recorded = delivery.status !== "PLANNED";

  const arrive = useMutation({
    mutationFn: async () => {
      const { readings, incompleteTankIds } = readingsToBody(rows, dateTimeLocalToIso);
      setIncomplete(incompleteTankIds);
      if (incompleteTankIds.length > 0) throw new Error(t("fuel.deliveries.readings.incompleteHint"));
      const { data } = await api.POST("/fuel/deliveries/{id}/arrive", {
        params: { path: { id: delivery.id } },
        body: { version: delivery.version, ...(readings.length > 0 ? { readings } : {}) },
      });
      return data!;
    },
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("fuel.deliveries.readings.arrived") });
    },
    onError: (caught) => {
      // Sunucu prob okuması bulamadı: kullanıcıyı elle girişe yönlendir (seçim kapanır).
      if (isApiError(caught, "READING_REQUIRED")) {
        setRows((current) => current.map((row) => ({ ...row, useProbe: false })));
        setError(t("fuel.deliveries.readings.probeMissing"));
        return;
      }
      setError(errorMessage(caught));
    },
  });

  return (
    <Section title={t("fuel.deliveries.steps.before")}>
      <p className="text-sm text-muted-foreground">{t("fuel.deliveries.readings.beforeHint")}</p>
      {recorded ? (
        <RecordedReadings delivery={delivery} phase="before" />
      ) : (
        <>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <TankReadingRows
            rows={rows}
            tanks={tanks}
            incompleteTankIds={incomplete}
            onChange={(tankId, changes) =>
              setRows((current) =>
                current.map((row) => (row.tankId === tankId ? { ...row, ...changes } : row)),
              )
            }
          />
          <div className="flex justify-end">
            <Button
              loading={arrive.isPending}
              disabled={!permissions.canManageDeliveries}
              onClick={() => {
                setError(null);
                arrive.mutate();
              }}
            >
              {t("fuel.deliveries.readings.saveBefore")}
            </Button>
          </div>
        </>
      )}
    </Section>
  );
}

/** Belgeye YAZILMIŞ okumalar (öncesi / sonrası) — hacim ve net hacim sunucuda hesaplanmıştır. */
function RecordedReadings({
  delivery,
  phase,
}: {
  delivery: Delivery;
  phase: "before" | "after";
}): ReactElement {
  const { t } = useTranslation();
  const rows = delivery.lines.flatMap((line) =>
    line.tanks.map((tank) => ({ ...tank, productName: line.productName })),
  );
  return (
    <div
      className="overflow-x-auto rounded-lg border"
      tabIndex={0}
      role="group"
      aria-label={t(`fuel.deliveries.readings.${phase}Label`)}
    >
      <table className="w-full text-sm">
        <caption className="sr-only">{t(`fuel.deliveries.readings.${phase}Label`)}</caption>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("fuel.deliveries.fields.tank")}</Th>
            <Th>{t("fuel.readings.measuredAt")}</Th>
            <Th align="right">{t("fuel.readings.levelMm")}</Th>
            <Th align="right">{t("fuel.readings.waterMm")}</Th>
            <Th align="right">{t("fuel.readings.temperatureC")}</Th>
            <Th align="right">{t("fuel.tanks.volume")}</Th>
            <Th align="right">{t("fuel.tanks.netVolume")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((tank) => (
            <tr key={tank.id} className="border-t">
              <td className="px-3 py-2">
                {t("fuel.deliveries.form.tankOption", {
                  tankNo: tank.tankNo,
                  product: tank.productName,
                })}
              </td>
              <td className="px-3 py-2">
                {formatDateTime(phase === "before" ? tank.beforeAt : tank.afterAt)}
              </td>
              <td className="px-3 py-2 text-right">
                <QuantityText value={phase === "before" ? tank.beforeLevelMm : tank.afterLevelMm} unit="MM" />
              </td>
              <td className="px-3 py-2 text-right">
                <QuantityText value={phase === "before" ? tank.beforeWaterMm : tank.afterWaterMm} unit="MM" />
              </td>
              <td className="px-3 py-2 text-right">
                <QuantityText
                  value={phase === "before" ? tank.beforeTemperatureC : tank.afterTemperatureC}
                  unit="CELSIUS"
                />
              </td>
              <td className="px-3 py-2 text-right">
                <QuantityText
                  value={phase === "before" ? tank.beforeVolumeL : tank.afterVolumeL}
                  unit="LITRE"
                />
              </td>
              <td className="px-3 py-2 text-right">
                <QuantityText
                  value={phase === "before" ? tank.beforeVolumeL15c : tank.afterVolumeL15c}
                  unit="LITRE"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── 3. Boşaltma ve oturma süresi ─────────────────────────────────────────────
function UnloadStep({
  delivery,
  settleMinutes,
  onNext,
}: {
  delivery: Delivery;
  settleMinutes: number | null;
  onNext: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const [now, setNow] = useState(() => new Date());
  const [error, setError] = useState<string | null>(null);

  // Sayaç SUNUCUDAKİ bitiş anından hesaplanır; burada yalnızca saniyede bir yeniden çizilir.
  useEffect(() => {
    if (delivery.unloadingFinishedAt === null) return;
    const handle = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(handle);
  }, [delivery.unloadingFinishedAt]);

  const settle = settleState({
    unloadingFinishedAt: delivery.unloadingFinishedAt,
    settleMinutes: settleMinutes ?? 0,
    now,
  });

  const act = useMutation({
    mutationFn: async (action: "start-unloading" | "finish-unloading") => {
      const request = { params: { path: { id: delivery.id } }, body: { version: delivery.version } };
      const { data } =
        action === "start-unloading"
          ? await api.POST("/fuel/deliveries/{id}/start-unloading", request)
          : await api.POST("/fuel/deliveries/{id}/finish-unloading", request);
      return data!;
    },
    onSuccess: async (_data, action) => {
      await refresh();
      toast({ tone: "success", title: t(`fuel.deliveries.unload.done.${action}`) });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const started = delivery.unloadingStartedAt !== null;
  const finished = delivery.unloadingFinishedAt !== null;

  return (
    <Section title={t("fuel.deliveries.steps.unload")}>
      <p className="text-sm text-muted-foreground">{t("fuel.deliveries.unload.hint")}</p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <Field label={t("fuel.deliveries.fields.arrivedAt")}>{formatDateTime(delivery.arrivedAt)}</Field>
        <Field label={t("fuel.deliveries.fields.unloadingStartedAt")}>
          {formatDateTime(delivery.unloadingStartedAt)}
        </Field>
        <Field label={t("fuel.deliveries.fields.unloadingFinishedAt")}>
          {formatDateTime(delivery.unloadingFinishedAt)}
        </Field>
      </dl>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={started || !permissions.canManageDeliveries}
          loading={act.isPending && act.variables === "start-unloading"}
          onClick={() => {
            setError(null);
            act.mutate("start-unloading");
          }}
        >
          <Play aria-hidden="true" />
          {t("fuel.deliveries.unload.start")}
        </Button>
        <Button
          variant="outline"
          disabled={!started || finished || !permissions.canManageDeliveries}
          loading={act.isPending && act.variables === "finish-unloading"}
          onClick={() => {
            setError(null);
            act.mutate("finish-unloading");
          }}
        >
          <Square aria-hidden="true" />
          {t("fuel.deliveries.unload.finish")}
        </Button>
      </div>

      {finished ? (
        <Card className="grid gap-2 p-4">
          <h3 className="text-sm font-semibold">{t("fuel.deliveries.unload.settleTitle")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("fuel.deliveries.unload.settleHint", { minutes: settleMinutes ?? 0 })}
          </p>
          {/* Sayaç metni canlı bölgedir: klavye / ekran okuyucu kullanıcısı da kalan süreyi duyar. */}
          <p className="text-2xl font-semibold tabular" role="timer" aria-live="off">
            {settle.elapsed
              ? t("fuel.deliveries.unload.settleReady")
              : formatCountdown(settle.remainingSeconds)}
          </p>
          <Button className="justify-self-start" disabled={!settle.elapsed} onClick={onNext}>
            {t("fuel.deliveries.unload.goToAfter")}
          </Button>
          {settle.elapsed ? null : (
            <p className="text-xs text-muted-foreground">{t("fuel.deliveries.unload.forceHint")}</p>
          )}
          <Button variant="ghost" size="sm" className="justify-self-start" onClick={onNext}>
            {t("fuel.deliveries.unload.goToAfterAnyway")}
          </Button>
        </Card>
      ) : null}
    </Section>
  );
}

// ── 4. Boşaltma sonrası okuma ve ölçüm ───────────────────────────────────────
function AfterStep({
  delivery,
  tanks,
  settleMinutes,
}: {
  delivery: Delivery;
  tanks: TankStatus[];
  settleMinutes: number | null;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const tankIds = useMemo(
    () => deliveryTankIds(delivery.lines.map((line) => ({ tankIds: line.tanks.map((tank) => tank.tankId) }))),
    [delivery.lines],
  );
  const [rows, setRows] = useState<TankReadingDraft[]>(() => initialReadingRows(tankIds, new Date(), true));
  const [sales, setSales] = useState<ManualSalesDraft[]>(() =>
    tankIds.map((tankId) => ({ tankId, litres: null })),
  );
  const [incomplete, setIncomplete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const measured = delivery.status !== "ARRIVED" && delivery.status !== "UNLOADING";

  const settle = settleState({
    unloadingFinishedAt: delivery.unloadingFinishedAt,
    settleMinutes: settleMinutes ?? 0,
    now: new Date(),
  });

  const measure = useMutation({
    mutationFn: async (force: { reason: string } | null) => {
      const { readings, incompleteTankIds } = readingsToBody(rows, dateTimeLocalToIso);
      setIncomplete(incompleteTankIds);
      if (incompleteTankIds.length > 0) throw new Error(t("fuel.deliveries.readings.incompleteHint"));
      const salesDuringUnload = manualSalesToBody(sales);
      const { data } = await api.POST("/fuel/deliveries/{id}/measure", {
        params: { path: { id: delivery.id } },
        body: {
          version: delivery.version,
          ...(readings.length > 0 ? { readings } : {}),
          ...(salesDuringUnload.length > 0 ? { salesDuringUnload } : {}),
          ...(force ? { force: true, forceReason: force.reason } : {}),
        },
      });
      return data!;
    },
    onSuccess: async () => {
      setForceOpen(false);
      setForceReason("");
      await refresh();
      toast({ tone: "success", title: t("fuel.deliveries.readings.measured") });
    },
    onError: (caught) => {
      if (isApiError(caught, "SETTLE_TIME_NOT_ELAPSED")) {
        setForceOpen(true);
        setError(
          t("fuel.deliveries.readings.settleNotElapsed", {
            remaining: formatCountdown(settle.remainingSeconds),
          }),
        );
        return;
      }
      if (isApiError(caught, "READING_REQUIRED")) {
        setRows((current) => current.map((row) => ({ ...row, useProbe: false })));
        setError(t("fuel.deliveries.readings.probeMissing"));
        return;
      }
      // Hata sayfada (pencerenin ARKASINDA) gösterilir: zorla ölçüm penceresi açık kalırsa kullanıcı hiçbir şey görmez.
      setForceOpen(false);
      setError(errorMessage(caught));
    },
  });

  if (measured)
    return (
      <Section title={t("fuel.deliveries.steps.after")}>
        <RecordedReadings delivery={delivery} phase="after" />
      </Section>
    );

  return (
    <Section title={t("fuel.deliveries.steps.after")}>
      <p className="text-sm text-muted-foreground">{t("fuel.deliveries.readings.afterHint")}</p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <TankReadingRows
        rows={rows}
        tanks={tanks}
        incompleteTankIds={incomplete}
        onChange={(tankId, changes) =>
          setRows((current) => current.map((row) => (row.tankId === tankId ? { ...row, ...changes } : row)))
        }
      />

      <Card className="grid gap-3 p-3 sm:p-4">
        <h3 className="text-sm font-semibold">{t("fuel.deliveries.readings.manualSales")}</h3>
        <p className="text-xs text-muted-foreground">{t("fuel.deliveries.readings.manualSalesHint")}</p>
        <ManualSalesRows
          rows={sales}
          tanks={tanks}
          onChange={(tankId, value) =>
            setSales((current) =>
              current.map((row) => (row.tankId === tankId ? { ...row, litres: value } : row)),
            )
          }
        />
      </Card>

      <div className="flex justify-end">
        <Button
          loading={measure.isPending}
          disabled={!permissions.canManageDeliveries}
          onClick={() => {
            setError(null);
            measure.mutate(null);
          }}
        >
          {t("fuel.deliveries.readings.measure")}
        </Button>
      </div>

      <Dialog
        open={forceOpen}
        onOpenChange={setForceOpen}
        title={t("fuel.deliveries.readings.forceTitle")}
        description={t("fuel.deliveries.readings.forceDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            measure.mutate({ reason: forceReason });
          }}
        >
          <FormField label={t("fuel.deliveries.readings.forceReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={500}
                value={forceReason}
                onChange={(event) => setForceReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setForceOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={measure.isPending}>
              {t("fuel.deliveries.readings.forceConfirm")}
            </Button>
          </div>
        </form>
      </Dialog>
    </Section>
  );
}

// ── 5. Sonuç ve fark ─────────────────────────────────────────────────────────
function ResultStep({
  delivery,
  totals,
}: {
  delivery: Delivery;
  totals: ReturnType<typeof deliveryTotals>;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("fuel.deliveries.steps.result")}>
      {delivery.suggestDispute ? (
        <Alert tone="warning" title={t("fuel.deliveries.result.suggestDisputeTitle")}>
          {t("fuel.deliveries.result.suggestDisputeBody")}
        </Alert>
      ) : null}
      {delivery.warnings.length > 0 ? (
        <Alert tone="info" title={t("fuel.deliveries.result.warnings")}>
          <ul className="list-inside list-disc">
            {delivery.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <LinesTable delivery={delivery} showMeasured />

      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("fuel.deliveries.result.totalDocument")}>{`${totals.documentL.formatTr(3)} L`}</Field>
        <Field label={t("fuel.deliveries.result.totalMeasured")}>
          {totals.measuredL === null ? "—" : `${totals.measuredL.formatTr(3)} L`}
        </Field>
        <Field label={t("fuel.deliveries.result.totalDifference")}>
          {totals.differenceL === null ? "—" : `${totals.differenceL.formatTr(3)} L`}
        </Field>
        <Field label={t("fuel.deliveries.fields.measuredAt")}>{formatDateTime(delivery.measuredAt)}</Field>
        {/* Uygulanan taban ve tolerans ÖLÇÜM ANINDA belgeye dondurulur: ayar sonradan değişse de bu değişmez. */}
        <Field label={t("fuel.deliveries.result.compareBasis")}>
          {delivery.compareBasis === null ? "—" : DELIVERY_COMPARE_BASIS_LABELS[delivery.compareBasis]}
        </Field>
        <Field label={t("fuel.deliveries.result.tolerancePerMille")}>
          {delivery.tolerancePerMille === null
            ? "—"
            : `‰${Decimal.from(delivery.tolerancePerMille).formatTr(2)}`}
        </Field>
        <Field label={t("fuel.deliveries.result.toleranceMinL")}>{litres(delivery.toleranceMinL)}</Field>
        <Field label={t("fuel.deliveries.fields.withinTolerance")}>
          {delivery.withinTolerance === null ? (
            "—"
          ) : (
            <StatusBadge tone={delivery.withinTolerance ? "success" : "danger"}>
              {delivery.withinTolerance
                ? t("fuel.deliveries.tolerance.inside")
                : t("fuel.deliveries.tolerance.outside")}
            </StatusBadge>
          )}
        </Field>
      </dl>
      <p className="text-xs text-muted-foreground">{t("fuel.deliveries.result.frozenHint")}</p>

      <h3 className="text-sm font-semibold">{t("fuel.deliveries.result.tankDetail")}</h3>
      <div
        className="overflow-x-auto rounded-lg border"
        tabIndex={0}
        role="group"
        aria-label={t("fuel.deliveries.result.tankDetail")}
      >
        <table className="w-full text-sm">
          <caption className="sr-only">{t("fuel.deliveries.result.tankDetail")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("fuel.deliveries.fields.tank")}</Th>
              <Th align="right">{t("fuel.deliveries.result.beforeVolume")}</Th>
              <Th align="right">{t("fuel.deliveries.result.afterVolume")}</Th>
              <Th align="right">{t("fuel.deliveries.result.salesDuringUnload")}</Th>
              <Th>{t("fuel.deliveries.result.salesSource")}</Th>
              <Th align="right">{t("fuel.deliveries.fields.measuredL")}</Th>
            </tr>
          </thead>
          <tbody>
            {delivery.lines.flatMap((line) =>
              line.tanks.map((tank) => (
                <tr key={tank.id} className="border-t">
                  <td className="px-3 py-2">
                    {t("fuel.deliveries.form.tankOption", {
                      tankNo: tank.tankNo,
                      product: line.productName,
                    })}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={tank.beforeVolumeL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={tank.afterVolumeL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={tank.salesDuringUnloadL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2">
                    {tank.salesSource === null ? "—" : DELIVERY_SALES_SOURCE_LABELS[tank.salesSource]}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={tank.measuredL} unit="LITRE" />
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ── 6. Onay / itiraz ─────────────────────────────────────────────────────────
function ConfirmStep({ delivery }: { delivery: Delivery }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: async (action: "confirm" | "dispute") => {
      const params = { path: { id: delivery.id } };
      if (action === "confirm") {
        const { data } = await api.POST("/fuel/deliveries/{id}/confirm", {
          params,
          body: { version: delivery.version },
        });
        return data!;
      }
      const { data } = await api.POST("/fuel/deliveries/{id}/dispute", {
        params,
        body: { version: delivery.version, reason },
      });
      return data!;
    },
    onSuccess: async (_data, action) => {
      setDisputeOpen(false);
      setReason("");
      await refresh();
      toast({ tone: "success", title: t(`fuel.deliveries.confirm.done.${action}`) });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const report = useMutation({
    mutationFn: async () =>
      (await api.GET("/fuel/deliveries/{id}/dispute-report", { params: { path: { id: delivery.id } } }))
        .data!,
    onSuccess: (file) => downloadBlob(base64ToBlob(file.contentBase64, file.mimeType), file.fileName),
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const canAct = delivery.status === "MEASURED" || delivery.status === "DISPUTED";

  return (
    <Section title={t("fuel.deliveries.steps.confirm")}>
      {error ? <Alert tone="danger">{error}</Alert> : null}

      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("fuel.deliveries.fields.costStatus")}>
          {DELIVERY_COST_STATUS_LABELS[delivery.costStatus]}
        </Field>
        <Field label={t("fuel.deliveries.fields.confirmedAt")}>{formatDateTime(delivery.confirmedAt)}</Field>
        <Field label={t("fuel.deliveries.fields.disputedAt")}>{formatDateTime(delivery.disputedAt)}</Field>
        <Field label={t("fuel.deliveries.fields.purchaseInvoice")}>
          {delivery.purchaseInvoiceId ? (
            <Link
              to="/faturalar/$id"
              params={{ id: delivery.purchaseInvoiceId }}
              className="underline underline-offset-4"
            >
              {t("fuel.deliveries.confirm.openInvoice")}
            </Link>
          ) : (
            "—"
          )}
        </Field>
      </dl>
      {delivery.costStatus === "PROVISIONAL" ? (
        <Alert tone="info">{t("fuel.deliveries.confirm.provisionalHint")}</Alert>
      ) : null}
      {delivery.status === "CONFIRMED" ? (
        <Alert tone="success">{t("fuel.deliveries.confirm.stockHint")}</Alert>
      ) : null}

      {permissions.canConfirmDeliveries && canAct ? (
        <div className="flex flex-wrap gap-2">
          <Button
            loading={act.isPending && act.variables === "confirm"}
            onClick={() => {
              setError(null);
              act.mutate("confirm");
            }}
          >
            <Check aria-hidden="true" />
            {delivery.status === "DISPUTED"
              ? t("fuel.deliveries.confirm.settleDispute")
              : t("fuel.deliveries.confirm.confirm")}
          </Button>
          {delivery.status === "MEASURED" ? (
            <Button variant="destructive" onClick={() => setDisputeOpen(true)}>
              <TriangleAlert aria-hidden="true" />
              {t("fuel.deliveries.confirm.dispute")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {delivery.status === "DISPUTED" ? (
        <Button
          variant="outline"
          className="justify-self-start"
          loading={report.isPending}
          onClick={() => report.mutate()}
        >
          <Download aria-hidden="true" />
          {t("fuel.deliveries.confirm.downloadReport")}
        </Button>
      ) : null}

      <MatchInvoice delivery={delivery} />

      <Dialog
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
        title={t("fuel.deliveries.confirm.disputeTitle")}
        description={t("fuel.deliveries.confirm.disputeDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            act.mutate("dispute");
          }}
        >
          <FormField label={t("fuel.deliveries.confirm.disputeReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={1000}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setDisputeOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" loading={act.isPending}>
              {t("fuel.deliveries.confirm.dispute")}
            </Button>
          </div>
        </form>
      </Dialog>
    </Section>
  );
}

/** Alış faturası eşleştirme: maliyet faturanın stok satırından gelir (ÖTV dâhil, KDV hariç). */
function MatchInvoice({ delivery }: { delivery: Delivery }): ReactElement | null {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const [invoiceId, setInvoiceId] = useState("");
  const invoices = usePurchaseInvoices(delivery.companyId, delivery.distributorCounterpartyId);

  const match = useMutation({
    mutationFn: async () => {
      const { data } = await api.POST("/fuel/deliveries/{id}/match-invoice", {
        params: { path: { id: delivery.id } },
        body: { invoiceId },
      });
      return data!;
    },
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("fuel.deliveries.confirm.matched") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  if (!permissions.canManageDeliveries || delivery.status === "CANCELLED") return null;
  const items = invoices.data?.items ?? [];

  return (
    <Card className="grid gap-3 p-3 sm:p-4">
      <h3 className="text-sm font-semibold">{t("fuel.deliveries.confirm.matchTitle")}</h3>
      <p className="text-xs text-muted-foreground">{t("fuel.deliveries.confirm.matchHint")}</p>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          match.mutate();
        }}
      >
        <FormField label={t("fuel.deliveries.confirm.invoice")} className="w-80">
          {(control) => (
            <Select
              {...control}
              value={invoiceId}
              onChange={(event) => setInvoiceId(event.target.value)}
              disabled={items.length === 0}
            >
              <option value="">{t("fuel.deliveries.confirm.selectInvoice")}</option>
              {items.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {`${invoice.invoiceNo ?? "—"} · ${formatDate(invoice.issueDate)} · ${Money.fromMinor(
                    invoice.payableAmount,
                  ).formatTr()}`}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <Button type="submit" variant="outline" loading={match.isPending} disabled={invoiceId === ""}>
          <FileText aria-hidden="true" />
          {t("fuel.deliveries.confirm.match")}
        </Button>
      </form>
      {items.length === 0 && invoices.data ? (
        <p className="text-xs text-muted-foreground">{t("fuel.deliveries.confirm.noInvoices")}</p>
      ) : null}
    </Card>
  );
}

function CancelDelivery({ delivery }: { delivery: Delivery }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const cancel = useMutation({
    mutationFn: async () => {
      const { data } = await api.POST("/fuel/deliveries/{id}/cancel", {
        params: { path: { id: delivery.id } },
        body: { version: delivery.version, reason },
      });
      return data!;
    },
    onSuccess: async () => {
      setOpen(false);
      await refresh();
      toast({ tone: "success", title: t("fuel.deliveries.detail.cancelled") });
      await navigate({ to: "/akaryakit/dolumlar" });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <>
      <Button variant="ghost" className="justify-self-start" onClick={() => setOpen(true)}>
        <X aria-hidden="true" />
        {t("fuel.deliveries.detail.cancel")}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t("fuel.deliveries.detail.cancelTitle")}
        description={t("fuel.deliveries.detail.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            cancel.mutate();
          }}
        >
          <FormField label={t("fuel.deliveries.detail.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" loading={cancel.isPending}>
              {t("fuel.deliveries.detail.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
